import { zodResolver } from '@hookform/resolvers/zod';
import { type Role, RoleKeySchema } from '@servicetim/shared';
import { useCreateProjectRole, useUpdateProjectRole } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { keyFromName } from '../lib/list-utils';
import { applyProblemErrors } from '../lib/rhf-errors';
import { FormRow } from '../ui/form-row';

const RoleFormSchema = z.object({
  key: RoleKeySchema,
  name: z.string().trim().min(1, 'A name is required').max(80),
});
type RoleFormValues = z.infer<typeof RoleFormSchema>;

export interface RoleDialogProps {
  projectId: string;
  /** `null` creates a role; otherwise renames it (keys are immutable). */
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoleDialog({ projectId, role, open, onOpenChange }: RoleDialogProps) {
  const id = useId();
  const create = useCreateProjectRole(projectId);
  const update = useUpdateProjectRole(projectId);
  const [omitPaths, setOmitPaths] = useState<Set<string>>(new Set());
  const [keyTouched, setKeyTouched] = useState(false);
  const form = useForm<RoleFormValues>({
    resolver: zodResolver(RoleFormSchema),
    defaultValues: { key: role?.key ?? '', name: role?.name ?? '' },
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the dialog opens or the row changes
  useEffect(() => {
    if (open) {
      form.reset({ key: role?.key ?? '', name: role?.name ?? '' });
      setKeyTouched(false);
      create.reset();
      update.reset();
      setOmitPaths(new Set());
    }
  }, [open, role]);

  const error = create.error ?? update.error;
  const errors = form.formState.errors;

  const submit = form.handleSubmit(async (values) => {
    try {
      if (role) {
        await update.mutateAsync({ id: role.id, patch: { name: values.name } });
        toast.success(`Role renamed to "${values.name}"`);
      } else {
        await create.mutateAsync(values);
        toast.success(`Role "${values.name}" created`);
      }
      onOpenChange(false);
    } catch (caught) {
      setOmitPaths(applyProblemErrors(caught, form.setError, { known: ['key', 'name'] }));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{role ? `Rename ${role.name}` : 'New project role'}</DialogTitle>
          <DialogDescription>
            Roles are granted permissions by the project's scheme and assigned to users or groups.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(event);
          }}
        >
          {error ? <ProblemBanner error={error} omitPaths={omitPaths} size="sm" /> : null}
          <FormRow label="Name" htmlFor={`${id}-name`} required error={errors.name?.message}>
            <Input
              id={`${id}-name`}
              {...form.register('name', {
                onChange: (e) => {
                  if (!role && !keyTouched) {
                    form.setValue('key', keyFromName(e.target.value), { shouldValidate: true });
                  }
                },
              })}
              maxLength={80}
              autoFocus
            />
          </FormRow>
          <FormRow
            label="Key"
            htmlFor={`${id}-key`}
            required
            hint="2-40 lowercase letters, digits or underscores; used by templates and rules."
            error={errors.key?.message}
          >
            <Input
              id={`${id}-key`}
              {...form.register('key', { onChange: () => setKeyTouched(true) })}
              maxLength={40}
              className="font-mono"
              disabled={role !== null}
              autoComplete="off"
            />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || update.isPending}>
              {role ? 'Rename' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
